import { z } from "zod";
import { AgencyIdSchema, ClientLinkIdSchema, ProjectIdSchema, ReportIdSchema } from "../primitives/ids.js";
import { ExternalProofFixKitIdSchema } from "./external-proof-fix-kit.js";
import { ExternalReadinessClientSafeFindingV1Schema, ExternalReadinessSeveritySchema, } from "./external-readiness-report.js";
import { EXTERNAL_PROOF_SALES_ROOM_RULE_SET_VERSION } from "./report-lifecycle.js";
export const ExternalProofSalesRoomIdSchema = z
    .string()
    .regex(/^srm_ext_[a-zA-Z0-9_-]+$/)
    .brand();
export const ExternalProofSalesRoomSectionIdSchema = z.enum([
    "siteSummary",
    "readinessSummary",
    "topIssues",
    "fixKitSummary",
    "agencyRecommendation",
    "clientActionItems",
    "blockedItems",
    "handoffPlaceholder",
]);
export const DEFAULT_EXTERNAL_PROOF_SALES_ROOM_VISIBLE_SECTIONS = [
    "siteSummary",
    "readinessSummary",
    "topIssues",
    "fixKitSummary",
    "agencyRecommendation",
    "clientActionItems",
    "blockedItems",
    "handoffPlaceholder",
];
export const ExternalProofSalesRoomLifecycleStatusSchema = z.enum([
    "SALES_ROOM_DRAFT",
    "READY_CLIENT_SAFE",
    "APPROVAL_REQUESTED",
    "CHANGE_REQUESTED",
    "APPROVED",
    "SUPERSEDED",
    "FAILED",
]);
export const ExternalProofSalesRoomApprovalStateSchema = z.enum([
    "idle",
    "approval_requested",
    "change_requested",
    "approved",
]);
export const ExternalProofSalesRoomSiteSummaryV1Schema = z
    .object({
    schemaId: z.literal("externalProofSalesRoom.siteSummary.v1"),
    headline: z.string().min(1),
    attachedUrlDisplay: z.string().min(1).optional(),
    scanStatus: z.enum(["PENDING", "PROCESSING", "COMPLETE", "FAILED", "UNKNOWN"]),
    fetchTimestamp: z.string().datetime().optional(),
    checkHighlights: z.array(z.string().min(1)).max(8).optional(),
})
    .strict();
export const ExternalProofSalesRoomTopIssueV1Schema = z
    .object({
    severity: ExternalReadinessSeveritySchema,
    summary: z.string().min(1),
    recommendation: z.string().min(1),
})
    .strict();
export const ExternalProofSalesRoomFixKitItemV1Schema = z
    .object({
    issueSummary: z.string().min(1),
    severity: ExternalReadinessSeveritySchema,
    recommendedFix: z.string().min(1),
    clientSafeExplanation: z.string().min(1),
})
    .strict();
export const ExternalProofSalesRoomClientActionItemV1Schema = z
    .object({
    label: z.string().min(1),
    detail: z.string().min(1),
})
    .strict();
export const ExternalProofSalesRoomVisibilityConfigV1Schema = z
    .object({
    schemaId: z.literal("externalProofSalesRoom.visibilityConfig.v1"),
    visibleSections: z.array(ExternalProofSalesRoomSectionIdSchema).min(1),
})
    .strict();
export const ExternalProofSalesRoomHandoffPlaceholderV1Schema = z
    .object({
    schemaId: z.literal("externalProofSalesRoom.handoffPlaceholder.v1"),
    summary: z.string().min(1),
    status: z.literal("COMING_SOON"),
})
    .strict();
/** Canonical internal storage document for Phase 6 external proof sales room. */
export const ExternalProofSalesRoomV1Schema = z
    .object({
    schemaVersion: z.literal("external-proof-sales-room.v1.0"),
    salesRoomId: ExternalProofSalesRoomIdSchema,
    agencyId: AgencyIdSchema,
    projectId: ProjectIdSchema,
    clientLinkId: ClientLinkIdSchema.optional(),
    sourceExternalReadinessReportId: ReportIdSchema,
    sourceExternalProofFixKitId: ExternalProofFixKitIdSchema,
    lifecycleStatus: ExternalProofSalesRoomLifecycleStatusSchema,
    approvalState: ExternalProofSalesRoomApprovalStateSchema,
    ruleSetVersion: z.literal(EXTERNAL_PROOF_SALES_ROOM_RULE_SET_VERSION),
    viewVersion: z.string().min(8).max(64),
    visibility: ExternalProofSalesRoomVisibilityConfigV1Schema,
    siteSummary: ExternalProofSalesRoomSiteSummaryV1Schema,
    readinessHeadline: z.string().min(1),
    readinessFindings: z.array(ExternalReadinessClientSafeFindingV1Schema),
    topIssues: z.array(ExternalProofSalesRoomTopIssueV1Schema),
    fixKitExecutiveSummary: z.string().min(1),
    fixKitItems: z.array(ExternalProofSalesRoomFixKitItemV1Schema),
    agencyRecommendation: z.string().min(1),
    clientRequestedItems: z.array(ExternalProofSalesRoomClientActionItemV1Schema),
    blockedItems: z.array(ExternalProofSalesRoomClientActionItemV1Schema),
    handoffPlaceholder: ExternalProofSalesRoomHandoffPlaceholderV1Schema,
    whatWeDoNotPromise: z.array(z.string().min(1)).min(1),
    agencyNotesClientSafe: z.string().max(2000).optional(),
    generatedAt: z.string().datetime(),
    updatedAt: z.string().datetime(),
})
    .strict();
export const ExternalProofSalesRoomInternalViewV1Schema = z
    .object({
    schemaId: z.literal("externalProofSalesRoom.internalView.v1"),
    salesRoomId: ExternalProofSalesRoomIdSchema,
    agencyId: AgencyIdSchema,
    projectId: ProjectIdSchema,
    clientLinkId: ClientLinkIdSchema.optional(),
    sourceExternalReadinessReportId: ReportIdSchema,
    sourceExternalProofFixKitId: ExternalProofFixKitIdSchema,
    lifecycleStatus: ExternalProofSalesRoomLifecycleStatusSchema,
    approvalState: ExternalProofSalesRoomApprovalStateSchema,
    viewVersion: z.string().min(8).max(64),
    visibility: ExternalProofSalesRoomVisibilityConfigV1Schema,
    siteSummary: ExternalProofSalesRoomSiteSummaryV1Schema,
    readinessHeadline: z.string().min(1),
    readinessFindings: z.array(ExternalReadinessClientSafeFindingV1Schema),
    topIssues: z.array(ExternalProofSalesRoomTopIssueV1Schema),
    fixKitExecutiveSummary: z.string().min(1),
    fixKitItems: z.array(ExternalProofSalesRoomFixKitItemV1Schema),
    agencyRecommendation: z.string().min(1),
    clientRequestedItems: z.array(ExternalProofSalesRoomClientActionItemV1Schema),
    blockedItems: z.array(ExternalProofSalesRoomClientActionItemV1Schema),
    handoffPlaceholder: ExternalProofSalesRoomHandoffPlaceholderV1Schema,
    whatWeDoNotPromise: z.array(z.string().min(1)).min(1),
    agencyNotesClientSafe: z.string().max(2000).optional(),
    generatedAt: z.string().datetime(),
    updatedAt: z.string().datetime(),
    clientSafe: z.literal(false),
})
    .strict();
export const ExternalProofSalesRoomClientSafeViewV1Schema = z
    .object({
    schemaId: z.literal("externalProofSalesRoom.clientSafeView.v1"),
    salesRoomId: ExternalProofSalesRoomIdSchema,
    projectId: ProjectIdSchema,
    lifecycleStatus: z.literal("READY_CLIENT_SAFE"),
    approvalState: ExternalProofSalesRoomApprovalStateSchema,
    viewVersion: z.string().min(8).max(64),
    visibleSections: z.array(ExternalProofSalesRoomSectionIdSchema).min(1),
    siteSummary: ExternalProofSalesRoomSiteSummaryV1Schema.optional(),
    readinessHeadline: z.string().min(1).optional(),
    readinessFindings: z.array(ExternalReadinessClientSafeFindingV1Schema).optional(),
    topIssues: z.array(ExternalProofSalesRoomTopIssueV1Schema).optional(),
    fixKitExecutiveSummary: z.string().min(1).optional(),
    fixKitItems: z.array(ExternalProofSalesRoomFixKitItemV1Schema).optional(),
    agencyRecommendation: z.string().min(1).optional(),
    clientRequestedItems: z.array(ExternalProofSalesRoomClientActionItemV1Schema).optional(),
    blockedItems: z.array(ExternalProofSalesRoomClientActionItemV1Schema).optional(),
    handoffPlaceholder: ExternalProofSalesRoomHandoffPlaceholderV1Schema.optional(),
    whatWeDoNotPromise: z.array(z.string().min(1)).min(1),
    generatedAt: z.string().datetime(),
    updatedAt: z.string().datetime(),
    clientSafe: z.literal(true),
})
    .strict();
export const GenerateExternalProofSalesRoomRequestV1Schema = z
    .object({
    schemaId: z.literal("externalProofSalesRoom.generateRequest.v1"),
    forceRegenerate: z.boolean().optional(),
    clientLinkId: ClientLinkIdSchema.optional(),
})
    .strict();
export const GenerateExternalProofSalesRoomResultV1Schema = z
    .object({
    schemaId: z.literal("externalProofSalesRoom.generateResult.v1"),
    salesRoomId: ExternalProofSalesRoomIdSchema,
    lifecycleStatus: ExternalProofSalesRoomLifecycleStatusSchema,
    viewVersion: z.string().min(8).max(64),
    sourceExternalReadinessReportId: ReportIdSchema,
    sourceExternalProofFixKitId: ExternalProofFixKitIdSchema,
    generatedAt: z.string().datetime(),
})
    .strict();
export const PatchExternalProofSalesRoomVisibilityRequestV1Schema = z
    .object({
    schemaId: z.literal("externalProofSalesRoom.patchVisibilityRequest.v1"),
    visibleSections: z.array(ExternalProofSalesRoomSectionIdSchema).min(1),
})
    .strict();
export const ExternalProofSalesRoomApprovalSubmissionV1Schema = z
    .object({
    schemaId: z.literal("externalProofSalesRoom.approvalSubmission.v1"),
    salesRoomId: ExternalProofSalesRoomIdSchema,
    viewVersion: z.string().min(8).max(64),
    approvedByName: z.string().min(1).max(120).optional(),
    note: z.string().max(500).optional(),
})
    .strict();
export const ExternalProofSalesRoomApprovalRecordV1Schema = z
    .object({
    schemaId: z.literal("externalProofSalesRoom.approvalRecord.v1"),
    salesRoomId: ExternalProofSalesRoomIdSchema,
    clientLinkId: ClientLinkIdSchema,
    viewVersion: z.string().min(8).max(64),
    status: z.literal("APPROVED"),
    approvedAt: z.string().datetime(),
    approvedByName: z.string().min(1).max(120).nullable(),
})
    .strict();
//# sourceMappingURL=external-proof-sales-room.js.map